import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import useCopy from '../../hooks/useCopy';

const COPY_LABEL = { copied: 'Copied', failed: 'Could not copy' };
// wide enough to read a long URL; it wraps anywhere
const TOOLTIP_PROPS = { tooltip: { sx: { maxWidth: 480 } } };

// Links come from the ad platform: only http(s) ones open, so a javascript: URL never runs on click
function isWebUrl(href) {
  try {
    return ['http:', 'https:'].includes(new URL(href).protocol);
  } catch {
    return false;
  }
}

/**
 * A URL in a table cell as two icons, copy and open in a new tab; their tooltips show the full URL.
 * Clicks stay inside the cell, so they do not open the row.
 */
export default function LinkCell({ href }) {
  const [copyState, copy] = useCopy();
  if (!href) return '—';
  const stop = (event) => event.stopPropagation();

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} onClick={stop}>
      <Tooltip componentsProps={TOOLTIP_PROPS}
               title={<FullLink action={COPY_LABEL[copyState] ?? 'Copy link'} href={href} />}>
        <IconButton size="small" onClick={() => copy(href)} aria-label={`Copy ${href}`}
                    color={copyState === 'copied' ? 'success' : 'primary'}>
          <ContentCopyIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      {isWebUrl(href) && (
        <Tooltip componentsProps={TOOLTIP_PROPS} title={<FullLink action="Open in new tab" href={href} />}>
          <IconButton size="small" component="a" href={href} target="_blank" rel="noopener noreferrer"
                      color="primary" aria-label={`Open ${href} in a new tab`}>
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function FullLink({ action, href }) {
  return (
    <>
      <Box component="span" sx={{ display: 'block', fontWeight: 600, mb: 0.25 }}>{action}</Box>
      <Box component="span" sx={{ display: 'block', wordBreak: 'break-all' }}>{href}</Box>
    </>
  );
}
